import styled from "styled-components";
import { motion } from "framer-motion";

export const Container = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[1]};
  padding-top: calc(
    env(safe-area-inset-top, 0px) + ${({ theme }) => theme.spacing[1]}
  );
  font-family: ${({ theme }) => theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  min-height: 84vh;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  /* Fill dialog paper so wizard footer stays pinned; height stays constant per step */
  @media (min-width: 769px) {
    flex: 1;
    min-height: 0; /* override base 84vh — dialog paper sets height */
    max-height: 100%;
    overflow: hidden;
    align-items: stretch;
  }

  @media (max-width: 768px) {
    /* Fill dialog paper; min-height 0 so flex children can shrink and scroll inside */
    height: 100%;
    max-height: 100%;
    min-height: 0;
    overflow: hidden;
    padding: 0;
    padding-top: calc(env(safe-area-inset-top, 0px));
    flex: 1;
    align-items: stretch;
  }

  @media (min-width: 600px) {
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[1.5]}`};
    padding-top: calc(
      env(safe-area-inset-top, 0px) + ${({ theme }) => theme.spacing[2]}
    );
  }
`;

export const Heading = styled(motion.h1)`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  text-align: center;
  font-weight: 600;
  line-height: 1.1;
  padding: ${({ theme }) => theme.spacing[2]};
  position: relative;
  width: 100%;
  @media (min-width: 600px) {
    font-size: 24px;
    padding: ${({ theme }) => theme.spacing[3]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

export const FormContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  max-width: 100vw;
  width: 100%;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
  @media (min-width: 600px) {
    max-width: 800px;
    padding: ${({ theme }) => theme.spacing[8]};
    gap: ${({ theme }) => theme.spacing[12]};
  }
`;

export const NoticeStepContainer = styled(motion.div)`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  background: none;
  border-radius: 0;
  margin-bottom: 0;
  gap: ${({ theme }) => theme.spacing[4]};
`;

export const FormContent = styled(motion.div)<{
  $wide?: boolean;
  $compact?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-height: 0;
  padding: 1rem;
  width: 100%;
  max-width: 100vw;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: ${({ $compact }) => ($compact ? "0.75rem 1rem" : "1rem")};
    gap: ${({ $compact }) => ($compact ? "1rem" : "1.5rem")};
  }

  @media (min-width: 600px) {
    max-width: ${({ $wide }) => ($wide ? "100%" : "720px")};
    padding: ${({ $wide, $compact }) =>
      $wide ? "0.75rem 0.5rem" : $compact ? "0.5rem 1.5rem" : "2rem"};
    gap: ${({ $wide, $compact }) =>
      $wide ? "1.5rem" : $compact ? "1rem" : "3rem"};
  }
  @media (min-width: 768px) {
    max-width: ${({ $wide }) => ($wide ? "100%" : "820px")};
    padding: ${({ $wide, $compact }) =>
      $wide ? "0.5rem 0.25rem" : $compact ? "0.25rem 1.5rem" : "2rem"};
    gap: ${({ $wide, $compact }) =>
      $wide ? "1.25rem" : $compact ? "0.75rem" : "2rem"};
  }
`;

export const FormSection = styled(motion.section)`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 4px 6px ${({ theme }) => theme.colors.shadowLight};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px ${({ theme }) => theme.colors.shadowMedium};
    transform: translateY(-2px);
  }
`;

export const FormDescription = styled(motion.p)`
  color: ${({ theme }) => theme.colors.secondaryText};
  font-size: 0.875rem;
  line-height: 1.5;
`;

export const FormGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 2rem;
  width: 100%;
  position: relative;
`;

export const FormRow = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

export const FormActions = styled(motion.div)`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[6]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const FormSidebar = styled(motion.aside)`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  height: fit-content;
  position: sticky;
  top: ${({ theme }) => theme.spacing[4]};
`;

export const FormMain = styled(motion.main)`
  flex: 1;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 4px 6px ${({ theme }) => theme.colors.shadowLight};
`;

export const FormProgress = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

export const FormCard = styled(motion.div)`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 4px 6px ${({ theme }) => theme.colors.shadowLight};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px ${({ theme }) => theme.colors.shadowMedium};
    transform: translateY(-2px);
  }
`;

export const Label = styled(motion.label)`
  font-size: ${({ theme }) => theme.typography.labelFontSize};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primaryText};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.errorFontSize};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

export const Input = styled(motion.input)<{ hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borderRadius.input};
  border: 2px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.border};
  font-size: ${({ theme }) => theme.typography.inputFontSize};
  transition: ${({ theme }) => theme.transitions.input};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textDark};
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.input};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  @media (max-width: 600px) {
    padding: ${({ theme }) => theme.spacing[3]}
      ${({ theme }) => theme.spacing[2]};
    font-size: 1rem;
    width: 85%;
  }

  @media (min-width: 768px) {
    width: 100%;
    max-width: 100%;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.inputPlaceholder};
    opacity: 0.7;
    transition: ${({ theme }) => theme.transitions.input};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 4px
      ${({ theme, hasError }) =>
        hasError
          ? theme.colors.inputErrorShadow
          : theme.colors.inputFocusShadow};
    transform: translateY(-1px);

    &::placeholder {
      transform: translateY(-4px);
      opacity: 0.5;
    }
  }

  &:hover {
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.inputHover};
  }
`;

export const TextArea = styled(motion.textarea)<{ hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  border-radius: ${({ theme }) => theme.borderRadius.input};
  border: 2px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.border};
  font-size: ${({ theme }) => theme.typography.inputFontSize};
  transition: ${({ theme }) => theme.transitions.input};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textDark};
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.input};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  resize: vertical;
  min-height: 120px;

  @media (max-width: 600px) {
    padding: ${({ theme }) => theme.spacing[3]}
      ${({ theme }) => theme.spacing[2]};
    font-size: 1rem;
    width: 85%;
  }

  @media (min-width: 768px) {
    width: 100%;
    max-width: 100%;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.inputPlaceholder};
    opacity: 0.7;
    transition: ${({ theme }) => theme.transitions.input};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 4px
      ${({ theme, hasError }) =>
        hasError
          ? theme.colors.inputErrorShadow
          : theme.colors.inputFocusShadow};
    transform: translateY(-1px);

    &::placeholder {
      transform: translateY(-4px);
      opacity: 0.5;
    }
  }

  &:hover {
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.inputHover};
  }
`;

export const Select = styled.select<{ hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: 8px;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.border};
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.primaryText};
  max-width: 100%;
  width: 100%;
  box-shadow: 0 1px 2px ${({ theme }) => theme.colors.shadowLight};
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${({ theme }) => theme.spacing[3]} center;
  background-size: 1em;
  padding-right: ${({ theme }) => theme.spacing[8]};

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError }) =>
      hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${({ theme, hasError }) =>
        hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    max-width: 500px;
  }
`;

export const FixedContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: white;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
  justify-content: flex-start;

  /* Grow to fill modal; scroll stays in FixedContent */
  @media (min-width: 768px) {
    flex: 1;
    min-height: 0;
    height: 100%;
    max-height: 100%;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    flex: 1;
    min-height: 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.gradientTealBlue[0]},
      ${({ theme }) => theme.colors.gradientTealBlue[1]}
    );
  }
`;

export const FixedHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[4]};
  padding-top: calc(env(safe-area-inset-top, 0px) + 56px);
  background-color: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  flex-shrink: 0;

  @media (min-width: 768px) {
    padding: ${({ theme }) => theme.spacing[2]}
      ${({ theme }) => theme.spacing[3]};
    padding-top: ${({ theme }) => theme.spacing[2]};
  }
`;

export const FixedContent = styled.div<{
  $isReview?: boolean;
  $compact?: boolean;
}>`
  flex: 1;
  padding: ${({ theme, $isReview, $compact }) =>
      $isReview
        ? theme.spacing[1]
        : $compact
          ? theme.spacing[1.5]
          : theme.spacing[2]}
    ${({ theme }) => theme.spacing[3]};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  background-color: white;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 768px) {
    padding: ${({ theme, $isReview, $compact }) =>
        $isReview
          ? theme.spacing[1]
          : $compact
            ? theme.spacing[2]
            : theme.spacing[4]}
      ${({ theme, $isReview }) =>
        $isReview ? theme.spacing[2] : theme.spacing[3]};
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    flex: 1 1 auto;
    justify-content: flex-start;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding-bottom: ${({ theme, $isReview, $compact }) =>
      $isReview
        ? theme.spacing[2]
        : $compact
          ? theme.spacing[1.5]
          : theme.spacing[3]};
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

export const FixedFooter = styled.div<{ $isReview?: boolean }>`
  padding: ${({ theme, $isReview }) =>
    $isReview ? theme.spacing[2] : theme.spacing[4]};
  padding-bottom: ${({ theme, $isReview }) =>
    $isReview
      ? `calc(${theme.spacing[2]} + env(safe-area-inset-bottom, 0px))`
      : `calc(${theme.spacing[4]} + env(safe-area-inset-bottom, 0px))`};
  background-color: white;
  position: relative;
  z-index: 10;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${({ $isReview }) => ($isReview ? "32px" : "60px")};
  flex-shrink: 0;
  margin-top: auto;

  @media (max-width: 768px) {
    padding-top: ${({ theme, $isReview }) =>
      $isReview ? theme.spacing[1] : theme.spacing[2]};
    padding-left: ${({ theme }) => theme.spacing[2]};
    padding-right: ${({ theme }) => theme.spacing[2]};
    padding-bottom: ${({ theme, $isReview }) =>
      $isReview
        ? `calc(72px + env(safe-area-inset-bottom, 0px))`
        : `calc(72px + env(safe-area-inset-bottom, 0px))`};
    min-height: ${({ $isReview }) => ($isReview ? "56px" : "72px")};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  @media (min-width: 768px) {
    min-height: 60px;
    padding: ${({ theme, $isReview }) =>
      $isReview ? theme.spacing[2] : theme.spacing[4]};
  }
`;

export const ComingSoonTag = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]}
    ${({ theme }) => theme.spacing[1.5]};
  background: ${({ theme }) => theme.colors.primary}10;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 500;
  margin-top: ${({ theme }) => theme.spacing[1]};
  border: 1px solid ${({ theme }) => theme.colors.primary}20;
  box-shadow: 0 1px 2px ${({ theme }) => theme.colors.shadowLight};
  transition: all 0.3s ease;
  position: absolute;
  right: ${({ theme }) => theme.spacing[2]};
  top: ${({ theme }) => theme.spacing[2]};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px ${({ theme }) => theme.colors.shadowDark};
  }
`;

export const RadioContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  height: fit-content;
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[2]};
    margin-top: ${({ theme }) => theme.spacing[2]};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: ${({ theme }) => theme.spacing[4]};
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

export const RadioOption = styled(motion.label)<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: 12px;
  border: 2px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, isSelected }) =>
    isSelected ? `${theme.colors.primary}10` : "white"};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 100px;
  justify-content: center;
  align-items: center;
  text-align: center;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[3]};
    min-height: 120px;
    border-radius: 16px;
    gap: ${({ theme }) => theme.spacing[1]};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.shadowLight};
  }

  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }

  &:active {
    @media (max-width: 768px) {
      transform: scale(0.98);
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primary : "transparent"};
    transition: all 0.3s ease;
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .radio-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid
      ${({ theme, isSelected }) =>
        isSelected ? theme.colors.primary : theme.colors.border};
    background: ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primary : "white"};
    margin-bottom: ${({ theme }) => theme.spacing[1]};
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      width: 24px;
      height: 24px;
      margin-bottom: ${({ theme }) => theme.spacing[2]};
    }

    &::after {
      content: "";
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: white;
      opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
      transform: scale(${({ isSelected }) => (isSelected ? 1 : 0.5)});
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    @media (max-width: 768px) {
      &::after {
        width: 12px;
        height: 12px;
      }
    }
  }

  .label-text {
    font-size: 1rem;
    font-weight: 500;
    color: ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primary : theme.colors.textDark};
    margin-bottom: ${({ theme }) => theme.spacing[1]};

    @media (max-width: 768px) {
      font-size: 1.125rem;
      font-weight: 600;
      margin-bottom: ${({ theme }) => theme.spacing[2]};
    }
  }

  .description {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.textMedium};
    margin-top: ${({ theme }) => theme.spacing[1]};
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 0.875rem;
      line-height: 1.4;
      margin-top: 0;
    }
  }
`;
